import React, { useEffect, useState } from 'react';

// Redux =>
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

// Actions =>
import { UUID } from '@Src/Global/Actions';
import { submitOrder, setDelivery } from '@Src/Cart/Actions';

// Components =>
import Template from '@Src/Cart/Template';
import Complete from '@Src/Cart/Complete';
import Summary from '@Src/Cart/Summary';
import Minicart from '@Src/Cart/Minicart';
import Loading from '@Shared/Loading';

// Routing
import { useLocation, useNavigate } from 'react-router-dom';

// Toast
import { toast } from 'react-toastify';

const Step4 = ({ cart: { delivery }, submitOrder, setDelivery }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [finalizing, setFinalizing] = useState(true);
    const [deliveryReady, setDeliveryReady] = useState(false);
    const [shouldClearStorage, setShouldClearStorage] = useState(false);

    useEffect(() => {
        if (delivery && delivery.location) {
            setDeliveryReady(true);
            return;
        }

        const storedDelivery = localStorage.getItem('checkoutDelivery');
        if (storedDelivery) {
            try {
                const parsed = JSON.parse(storedDelivery);
                if (parsed?.method && parsed?.location) {
                    setDelivery(parsed.method, parsed.location)
                        .then(() => setDeliveryReady(true))
                        .catch(() => setDeliveryReady(true));
                    return;
                }
            } catch (error) {
                console.error('No se pudo restaurar la entrega almacenada', error);
            }
        }

        setDeliveryReady(true);
    }, [delivery, setDelivery]);

    useEffect(() => {
        if (!deliveryReady) {
            return;
        }

        const params = new URLSearchParams(location.search);
        const statusParam = (params.get('status') || '').toLowerCase();
        const isSuccess = statusParam === 'success' || statusParam === 'approved';
        const isPending = statusParam === 'pending' || statusParam === 'in_process';
        const isFailure = statusParam === 'failure' || statusParam === 'rejected';
        const isCanceled = statusParam === 'cancel' || statusParam === 'canceled' || statusParam === 'cancelled';

        if (!isSuccess && !isPending) {
            if (isCanceled) {
                toast.warning('El pago fue cancelado. Te regresamos al carrito para que puedas intentar nuevamente.');
            } else if (isFailure) {
                toast.error('El pago no se efectuó. Te regresamos al carrito para que puedas intentar nuevamente.');
            }

            navigate('/cart/step-2');
            setFinalizing(false);
            return;
        }

        if (isPending) {
            toast.info('Tu pago quedó pendiente de confirmación. Nos comunicaremos contigo para confirmar el estado de tu pedido.');
        }

        let mounted = true;
        setFinalizing(true);

        submitOrder().then((res) => {
            if (!mounted) {
                return;
            }

            if (!res.success) {
                toast.error(res.message);
                navigate('/cart/step-2');
            } else {
                toast.success(res.message);
                setShouldClearStorage(true);
            }
        }).catch(() => {
            if (!mounted) {
                return;
            }
            toast.error('No fue posible confirmar tu pedido, intenta nuevamente.');
            navigate('/cart/step-2');
        }).finally(() => {
            if (mounted) {
                setFinalizing(false);
            }
        });

        return () => {
            mounted = false;
        };
    }, [deliveryReady, location.search, navigate, submitOrder]);

    useEffect(() => {
        return () => {
            if (!shouldClearStorage) {
                return;
            }
            localStorage.removeItem('lastBasket');
            localStorage.removeItem('checkoutDelivery');
            localStorage.removeItem('checkoutBasket');
        }
    }, [shouldClearStorage])

    if (finalizing) {
        return <Loading />;
    }

    return (
        <Template
            title={'¡Compra completada!'}
            className={'cart-step-4'}
            step={4}
            left={[
                <Complete key={UUID()} />,
            ]}
            right={[
                <Minicart key={UUID()} />,
                <Summary key={UUID()} />,
            ]}
        />
    );
};

// mapStateToProps =>
function mapStateToProps(state) {
    return state;
}

// Bind actions to be used along redux =>
function matchDispatchToProps(dispatch) {
    return bindActionCreators({
        // Actions
        submitOrder,
        setDelivery,
    }, dispatch);
}

export default connect(mapStateToProps, matchDispatchToProps)(Step4);
